/**
 * What actually happened, measured rather than remembered.
 *
 * Every number in this game's design so far has been an argument: floors
 * should last about a minute, the first kill should land in five seconds, a
 * run should end somewhere around floor ten. Some of those are right. Nobody
 * knows which, because nothing has ever counted.
 *
 * This counts. It is deliberately local and deliberately dumb — no network, no
 * identifiers, no storage that outlives the tab unless somebody asks for it —
 * because the point is not analytics, it is being able to answer "is the
 * fourth floor the one where people die" without playing forty runs and
 * forming an impression.
 *
 * Two rules it is built under.
 *
 * It costs nothing per frame. Frame times go into a preallocated ring; every
 * other measurement is arithmetic on numbers that already exist. A telemetry
 * system that shows up in the thing it is measuring is measuring itself.
 *
 * And it is the *same* instrument in the browser and in the practice bot. The
 * balance numbers `tools/sim.ts` prints and the numbers a real session
 * produces come out of one implementation, so a discrepancy between them is a
 * discrepancy in the game rather than in the two ways of looking at it.
 */

/** How many frame times to keep. Two minutes at sixty, and then it wraps. */
const FRAMES = 7200;

export interface WeaponUse {
  seconds: number;
  shots: number;
  kills: number;
  hits: number;
}

export interface FloorRecord {
  depth: number;
  map: string;
  contract: string;
  zones: number;
  /** The pairing the floor was built around, if it got one. */
  bind: string;
  cleared: boolean;
  seconds: number;
  kills: number;
  /** Health lost on this floor, and where it came from. */
  damage: number;
  damageBy: Record<string, number>;
  /**
   * The fraction of the floor with something alive and within earshot.
   *
   * The single most useful number here. A floor that is 90% combat is
   * exhausting and a floor that is 30% combat is a walk, and neither of those
   * is visible in "the floor took fifty-five seconds".
   */
  combat: number;
  /** The longest unbroken stretch with nothing alive at all. */
  quiet: number;
  /** Metres walked, and the straight-line distance those metres achieved. */
  walked: number;
  progress: number;
  /** Times the player turned round and went back the way they came. */
  reversals: number;
  /** Times the floor had to drag the survivors to the player, or give up. */
  rescues: number;
  conceded: boolean;
}

export interface RunRecord {
  seed: string;
  /** Seconds from the run starting to each of the four things that matter. */
  toInput: number;
  toEnemy: number;
  toShot: number;
  toKill: number;
  seconds: number;
  score: number;
  kills: number;
  depth: number;
  died: boolean;
  killedBy: string;
  cards: string[];
  offered: string[][];
  weapons: Record<string, WeaponUse>;
  floors: FloorRecord[];
  frames: { median: number; p95: number; p99: number; worst: number; count: number };
}

const NOTHING = -1;

export class Telemetry {
  private readonly times = new Float32Array(FRAMES);
  private frames = 0;

  private clock = 0;
  private seed = '';
  private toInput = NOTHING;
  private toEnemy = NOTHING;
  private toShot = NOTHING;
  private toKill = NOTHING;
  private cards: string[] = [];
  private offered: string[][] = [];
  private weapons: Record<string, WeaponUse> = {};
  private floors: FloorRecord[] = [];
  private floor: FloorRecord | null = null;

  /** Per-floor working state, kept off the record until the floor ends. */
  private since = 0;
  private quiet = 0;
  private combat = 0;
  private lastAt: [number, number, number] | null = null;
  private startAt: [number, number, number] | null = null;
  /** Where the heading was last read, two metres ago. */
  private turnAt: [number, number, number] | null = null;
  private heading = 0;
  private turned = false;

  /** Begin a run. Everything from here is one record. */
  run(seed: string): void {
    this.clock = 0;
    this.seed = seed;
    this.toInput = NOTHING;
    this.toEnemy = NOTHING;
    this.toShot = NOTHING;
    this.toKill = NOTHING;
    this.cards = [];
    this.offered = [];
    this.weapons = {};
    this.floors = [];
    this.floor = null;
    this.frames = 0;
  }

  /**
   * One frame's wall time, in milliseconds.
   *
   * Anything over a fifth of a second was a floor being built rather than a
   * frame being drawn, and including those would make every percentile a
   * measure of load time. The governor draws the same line for the same
   * reason.
   */
  frame(ms: number): void {
    if (ms >= 200) return;
    this.times[this.frames % FRAMES] = ms;
    this.frames++;
  }

  /**
   * The run's own clock, advanced with the simulation rather than the wall.
   *
   * A paused game is not a slow player, and a frame the browser skipped while
   * the tab was in the background is not fifteen seconds of hesitation.
   */
  tick(dt: number, enemiesAlive: number, at: [number, number, number]): void {
    this.clock += dt;
    const f = this.floor;
    if (!f) return;
    f.seconds += dt;
    if (enemiesAlive > 0) {
      this.combat += dt;
      this.since = 0;
    } else {
      this.since += dt;
      if (this.since > this.quiet) this.quiet = this.since;
    }

    /*
     * Distance walked against distance achieved.
     *
     * A floor where those two numbers are close is a floor the player crossed;
     * one where the first is three times the second is a floor they searched.
     * The whole zone system exists to make the second kind rare, and this is
     * the only way to know whether it worked.
     */
    if (!this.startAt) {
      this.startAt = [at[0], at[1], at[2]];
      this.lastAt = [at[0], at[1], at[2]];
      this.turnAt = [at[0], at[1], at[2]];
      return;
    }
    const last = this.lastAt!;
    const step = Math.hypot(at[0] - last[0], at[1] - last[1]);
    if (step > 0.02) {
      f.walked += step;
      last[0] = at[0];
      last[1] = at[1];
      last[2] = at[2];
    }

    /*
     * A reversal is measured over metres, not over frames.
     *
     * Comparing this frame's heading to the last one's asks whether the player
     * changed direction in sixteen milliseconds, which they always have and
     * which means nothing: at sixty frames a step is eight centimetres and the
     * heading of an eight-centimetre step is noise. So the heading is only
     * re-read once a real distance has been covered, and a reversal is a turn
     * of more than a right angle between two of *those*.
     */
    const turn = this.turnAt!;
    const run = Math.hypot(at[0] - turn[0], at[1] - turn[1]);
    if (run > 2) {
      const going = Math.atan2(at[1] - turn[1], at[0] - turn[0]);
      let swing = going - this.heading;
      while (swing > Math.PI) swing -= Math.PI * 2;
      while (swing < -Math.PI) swing += Math.PI * 2;
      if (this.turned && Math.abs(swing) > 1.9) f.reversals++;
      this.heading = going;
      this.turned = true;
      turn[0] = at[0];
      turn[1] = at[1];
      turn[2] = at[2];
    }
  }

  floorStart(depth: number, map: string, contract: string, zones: number, bind = ''): void {
    this.closeFloor(false);
    this.floor = {
      depth, map, contract, zones, bind,
      cleared: false, seconds: 0, kills: 0, damage: 0, damageBy: {},
      combat: 0, quiet: 0, walked: 0, progress: 0, reversals: 0,
      rescues: 0, conceded: false,
    };
    this.since = 0;
    this.quiet = 0;
    this.combat = 0;
    this.lastAt = null;
    this.startAt = null;
    this.turnAt = null;
    this.turned = false;
  }

  floorEnd(cleared: boolean): void {
    this.closeFloor(cleared);
  }

  private closeFloor(cleared: boolean): void {
    const f = this.floor;
    if (!f) return;
    f.cleared = cleared;
    f.combat = f.seconds > 0 ? this.combat / f.seconds : 0;
    f.quiet = this.quiet;
    if (this.startAt && this.lastAt) {
      f.progress = Math.hypot(this.lastAt[0] - this.startAt[0], this.lastAt[1] - this.startAt[1]);
    }
    this.floors.push(f);
    this.floor = null;
  }

  /** The four firsts, each recorded once and never overwritten. */
  input(): void {
    if (this.toInput === NOTHING) this.toInput = this.clock;
  }

  sawEnemy(): void {
    if (this.toEnemy === NOTHING) this.toEnemy = this.clock;
  }

  shot(weapon: string): void {
    if (this.toShot === NOTHING) this.toShot = this.clock;
    this.use(weapon).shots++;
  }

  hit(weapon: string): void {
    this.use(weapon).hits++;
  }

  kill(weapon: string): void {
    if (this.toKill === NOTHING) this.toKill = this.clock;
    this.use(weapon).kills++;
    if (this.floor) this.floor.kills++;
  }

  /** Seconds the player spent holding a particular gun. */
  holding(weapon: string, dt: number): void {
    this.use(weapon).seconds += dt;
  }

  /**
   * Damage taken, attributed.
   *
   * By the thing that dealt it rather than by a category, because "you lost
   * forty per cent of your health to Sentinels" is a design finding and "you
   * lost forty per cent to ranged attacks" is not.
   */
  hurt(amount: number, source: string): void {
    const f = this.floor;
    if (!f) return;
    f.damage += amount;
    f.damageBy[source] = (f.damageBy[source] ?? 0) + amount;
  }

  rescued(conceded: boolean): void {
    if (!this.floor) return;
    this.floor.rescues++;
    if (conceded) this.floor.conceded = true;
  }

  offer(ids: string[]): void {
    this.offered.push(ids);
  }

  took(id: string): void {
    this.cards.push(id);
  }

  private use(weapon: string): WeaponUse {
    let w = this.weapons[weapon];
    if (!w) this.weapons[weapon] = w = { seconds: 0, shots: 0, kills: 0, hits: 0 };
    return w;
  }

  /** Everything, as data. */
  snapshot(state: { score: number; kills: number; depth: number; phase: string; killedBy: string }): RunRecord {
    const kept = Math.min(this.frames, FRAMES);
    const sorted = Array.from(this.times.subarray(0, kept)).sort((a, b) => a - b);
    const at = (p: number): number => (kept ? sorted[Math.min(kept - 1, Math.floor(kept * p))] : 0);
    return {
      seed: this.seed,
      toInput: this.toInput,
      toEnemy: this.toEnemy,
      toShot: this.toShot,
      toKill: this.toKill,
      seconds: this.clock,
      score: state.score,
      kills: state.kills,
      depth: state.depth,
      died: state.phase === 'dead',
      killedBy: state.killedBy,
      cards: [...this.cards],
      offered: this.offered.map((o) => [...o]),
      weapons: JSON.parse(JSON.stringify(this.weapons)) as Record<string, WeaponUse>,
      floors: [...this.floors, ...(this.floor ? [this.floor] : [])],
      frames: {
        median: at(0.5), p95: at(0.95), p99: at(0.99),
        worst: kept ? sorted[kept - 1] : 0, count: kept,
      },
    };
  }
}

/**
 * One run's numbers, as a table a person can read.
 *
 * Deliberately not JSON: the point of measuring is that somebody looks, and
 * nobody looks at four kilobytes of nested objects in a console.
 */
export function report(r: RunRecord): string {
  const s = (v: number): string => (v < 0 ? '—' : `${v.toFixed(1)}s`);
  const pct = (v: number): string => `${Math.round(v * 100)}%`;
  const lines: string[] = [];
  lines.push(`run ${r.seed} · floor ${r.depth} · ${r.score} points · ${r.kills} kills · ${s(r.seconds)}`);
  lines.push(`  first input ${s(r.toInput)} · first enemy ${s(r.toEnemy)} · first shot ${s(r.toShot)} · first kill ${s(r.toKill)}`);
  if (r.died) lines.push(`  died to ${r.killedBy || 'something unnamed'}`);
  lines.push('');
  lines.push('  floor  map              contract  zones  time   kills  dmg   combat  quiet  walked  turns');
  for (const f of r.floors) {
    lines.push(
      `  ${String(f.depth).padStart(5)}  ${f.map.padEnd(16)} ${f.contract.padEnd(8)} ` +
      `${String(f.zones).padStart(5)}  ${f.seconds.toFixed(0).padStart(4)}s  ` +
      `${String(f.kills).padStart(5)}  ${f.damage.toFixed(0).padStart(3)}   ` +
      `${pct(f.combat).padStart(6)}  ${f.quiet.toFixed(0).padStart(4)}s  ` +
      `${f.walked.toFixed(0).padStart(5)}m  ${String(f.reversals).padStart(5)}` +
      `${f.conceded ? '   conceded' : ''}`,
    );
  }
  const hurt = new Map<string, number>();
  for (const f of r.floors) for (const [k, v] of Object.entries(f.damageBy)) hurt.set(k, (hurt.get(k) ?? 0) + v);
  const worst = [...hurt].sort((a, b) => b[1] - a[1]).slice(0, 6);
  if (worst.length) {
    lines.push('');
    lines.push(`  damage taken: ${worst.map(([k, v]) => `${k} ${v.toFixed(0)}`).join(' · ')}`);
  }
  const guns = Object.entries(r.weapons)
    .filter(([, w]) => w.shots > 0)
    .sort((a, b) => b[1].seconds - a[1].seconds)
    .slice(0, 6);
  if (guns.length) {
    lines.push(`  weapons: ${guns.map(([k, w]) => `${k} ${w.seconds.toFixed(0)}s ${w.kills}k ${w.shots ? Math.round((w.hits / w.shots) * 100) : 0}%`).join(' · ')}`);
  }
  if (r.cards.length) lines.push(`  build: ${r.cards.join(', ')}`);
  const f = r.frames;
  if (f.count) {
    lines.push(`  frames (${f.count}): median ${f.median.toFixed(1)}ms · p95 ${f.p95.toFixed(1)} · p99 ${f.p99.toFixed(1)} · worst ${f.worst.toFixed(1)}`);
  }
  return lines.join('\n');
}
